import { Alert, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { MdOpenInNew } from "react-icons/md";
import EmptyState from "shared/components/EmptyState";
import Spin from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { PPE_ALERT_STATUS, PPE_ALERT_TYPE } from "utils/constants";
import { usePPEGEL } from "./PPEGELProvider";

const typeTagColor = {
  [PPE_ALERT_TYPE.GELS_SANCTIONS]: "error",
  [PPE_ALERT_TYPE.PPE_NATIONALES]: "warning",
  [PPE_ALERT_TYPE.PPE_INTERNATIONALES]: "warning",
};

const statusTagColor = {
  [PPE_ALERT_STATUS.RAISED]: "success",
  [PPE_ALERT_STATUS.CONFIRMED]: "error",
};

const ScoreIndicator = ({ score }) => (
  <div className="ppegel-alerts-table__score-cell">
    <span className="ppegel-alerts-table__score">{score}%</span>
  </div>
);

const AlertRow = ({ alert, index, onClick, t }) => {
  const isTreated = alert.status !== PPE_ALERT_STATUS.PENDING;
  const statusColor = statusTagColor[alert.status];
  const statusLabel = t(`ALERT_STATUSES.${alert.status}`);

  return (
    <div
      className={`ppegel-alerts-table__row ${
        isTreated ? "ppegel-alerts-table__row--treated" : ""
      }`}
    >
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--score">
        <ScoreIndicator score={alert.score} />
      </div>
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--result">
        {alert.matchedName}
      </div>
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--type">
        <Tag color={typeTagColor[alert.type] || "default"}>
          {t(`ALERT_TYPES.${alert.type}`) || alert.type}
        </Tag>
      </div>
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--source">
        {alert.source}
      </div>
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--status">
        {statusColor && <Tag color={statusColor}>{statusLabel}</Tag>}
      </div>
      <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--action">
        <MdOpenInNew
          className="ppegel-alerts-table__action-icon"
          onClick={() => onClick(index)}
        />
      </div>
    </div>
  );
};

const AlertsTable = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PPEGEL);
  const TABLE = t("TABLE", { returnObjects: true });
  const {
    persons,
    selectedPersonIndex,
    selectAlert,
    isBlocked,
    blockedByName,
    loading,
  } = usePPEGEL();

  const selectedPerson = persons[selectedPersonIndex];
  const alerts = selectedPerson?.alerts || [];

  if (loading) {
    return (
      <div className="ppegel-alerts-table__loading">
        <Spin />
        <p>{TABLE.LOADING}</p>
      </div>
    );
  }

  if (!alerts.length) {
    return (
      <EmptyState title={TABLE.EMPTY_TITLE} subtitle={TABLE.EMPTY_SUBTITLE} />
    );
  }

  const hasConfirmedGEL = alerts.some(
    (a) =>
      a.type === PPE_ALERT_TYPE.GELS_SANCTIONS &&
      a.status === PPE_ALERT_TYPE.CONFIRMED,
  );

  return (
    <div className="ppegel-alerts-table">
      {isBlocked && hasConfirmedGEL && (
        <Alert
          type="error"
          showIcon
          className="ppegel-alerts-table__cascade-banner"
          message={format(TABLE.CASCADE_BANNER, { name: blockedByName })}
        />
      )}

      <div className="ppegel-alerts-table__header">
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--score">
          {TABLE.HEADERS.SCORE}
        </div>
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--result">
          {TABLE.HEADERS.RESULT}
        </div>
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--type">
          {TABLE.HEADERS.TYPE}
        </div>
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--source">
          {TABLE.HEADERS.SOURCE}
        </div>
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--status">
          {TABLE.HEADERS.STATUS}
        </div>
        <div className="ppegel-alerts-table__cell ppegel-alerts-table__cell--action"></div>
      </div>

      <div className="ppegel-alerts-table__body">
        {alerts.map((alert, index) => (
          <AlertRow
            key={alert.id || index}
            alert={alert}
            index={index}
            onClick={selectAlert}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default AlertsTable;
